import type {
  IDraftItem,
  IDraftLive,
  ILiveCategory,
  ILiveDish,
  ILiveOther,
  ILiveSize,
  IMenuDraft,
  ISyncCounts,
  TItemFlag,
} from '@/interfaces/IMenuImport';

/**
 * Pure rules of an update session (menu update from photos, plan §3.5 and
 * §7.2): a draft matched against the store's live menu. The server computes
 * the links, changes and holds; these functions read them for the UI and
 * mirror the API's publish counts (`sync-plan.ts`): keep both equal.
 */

/** Hold flags, in the order the server sets them. */
export const HOLD_FLAGS: readonly TItemFlag[] = [
  'price_jump',
  'price_down',
  'sizes_differ',
  'price_promo',
];

export type TLiveItemState =
  | 'new'
  | 'suggested'
  | 'priceChanged'
  /** Same prices, hidden or shown again by publishing. */
  | 'visibilityChanged'
  | 'unchanged'
  | 'held';

/** The draft was matched against a store with dishes. */
export function isUpdateDraft(
  draft: IMenuDraft | null
): draft is IMenuDraft & { live: IDraftLive } {
  return !!draft && !!draft.live;
}

/** A linked dish whose prices publishing leaves as they are. */
export function isHeld(item: Pick<IDraftItem, 'flags'>): boolean {
  return item.flags.some((flag) => HOLD_FLAGS.includes(flag));
}

/**
 * The dish shows the live line (a hold, or a suggestion): the generic check
 * line and «Está bien» are not rendered for it.
 */
export function hasLiveLine(item: Pick<IDraftItem, 'flags' | 'live'>): boolean {
  const live = item.live;
  if (!live) return false;
  return live.dish === null ? live.candidateIds.length > 0 : isHeld(item);
}

/** A dish publishing adds (not linked to a dish of the menu). */
export function isNewDish(item: IDraftItem): boolean {
  return !item.live || item.live.dish === null;
}

export function liveItemState(item: IDraftItem): TLiveItemState {
  const live = item.live;
  if (!live || live.dish === null) {
    return live && live.candidateIds.length > 0 ? 'suggested' : 'new';
  }
  if (isHeld(item)) return 'held';
  if (live.changes.length > 0) return 'priceChanged';
  return linkedOutcome(item, live.dish).visibilityChanges
    ? 'visibilityChanged'
    : 'unchanged';
}

/** The price diners see today: the single choice's, else the dish's own. */
export function todayPrice(dish: ILiveDish): number {
  return dish.sizes.length === 1 ? dish.sizes[0].price : dish.price;
}

/**
 * `(to − from) / from` as a fraction; null when `from ≤ 0` or the change
 * rounds to 0 %.
 */
export function percentChange(from: number, to: number): number | null {
  if (from <= 0) return null;
  const fraction = (to - from) / from;
  return Math.round(fraction * 100) === 0 ? null : fraction;
}

/** `Intl.NumberFormat` options with ES2020's `signDisplay` (not in this project's lib). */
interface ISignedNumberFormatOptions extends Intl.NumberFormatOptions {
  signDisplay: 'exceptZero';
}

/**
 * «+12 %», «-8 %»: the percent in the locale, always signed
 * (`signDisplay: 'exceptZero'`). Engines without `signDisplay` print no
 * sign for a rise: it is added in front.
 */
export function formatPercentChange(fraction: number, locale: string): string {
  const options: ISignedNumberFormatOptions = {
    style: 'percent',
    maximumFractionDigits: 0,
    signDisplay: 'exceptZero',
  };
  const text = new Intl.NumberFormat(locale, options).format(fraction);
  return fraction > 0 && !text.includes('+') ? `+${text}` : text;
}

function itemIsShown(draft: IMenuDraft, item: IDraftItem): boolean {
  if (item.hidden) return false;
  const category = draft.categories[item.categoryId];
  return !!category && !category.hidden;
}

function itemIsPriced(item: IDraftItem): boolean {
  return item.sizes.length > 0
    ? item.sizes.every((size) => size.price !== null)
    : item.price !== null;
}

/** What publishing leaves on a linked dish (the API's `linkedUpdate`). */
export interface ILinkedOutcome {
  /** The price diners see after publishing (the API's `publishedPrice`). */
  price: number;
  /** Its visibility after publishing. */
  visible: boolean;
  /** Publishing changes its visibility. */
  visibilityChanges: boolean;
}

/**
 * The price diners see after publishing (its changes, none while held): a
 * dish with sizes, its first active size's (the dish's own without one); a
 * single choice, the choice's; else the dish's own. The API's
 * `publishedPrice` (sync-plan.ts): keep both equal.
 */
function publishedPrice(item: IDraftItem, dish: ILiveDish): number {
  const changes = item.live && !isHeld(item) ? item.live.changes : [];
  const sizePrice = (size: ILiveSize): number => {
    const change = changes.find(
      (candidate) =>
        candidate.kind === 'size' &&
        candidate.optionItemId === size.optionItemId
    );
    return change ? change.to : size.price;
  };
  if (dish.sizes.length >= 2) {
    const first = dish.sizes.find((size) => size.active);
    return first ? sizePrice(first) : dish.price;
  }
  if (dish.sizes.length === 1) return sizePrice(dish.sizes[0]);
  const change = changes.find((candidate) => candidate.kind === 'price');
  return change ? change.to : dish.price;
}

export function linkedOutcome(
  item: IDraftItem,
  dish: ILiveDish
): ILinkedOutcome {
  const price = publishedPrice(item, dish);
  const to = !item.hidden;
  // A hidden dish is never shown at a price of 0.
  const visibilityChanges = to !== dish.visible && !(to && price <= 0);
  return {
    price,
    visible: visibilityChanges ? to : dish.visible,
    visibilityChanges,
  };
}

/** The linked dishes in menu order. */
function linkedItems(
  draft: IMenuDraft
): { item: IDraftItem; dish: ILiveDish }[] {
  const linked: { item: IDraftItem; dish: ILiveDish }[] = [];
  for (const categoryId of draft.categoryOrder) {
    const category = draft.categories[categoryId];
    if (!category) continue;
    for (const itemId of category.itemIds) {
      const item = draft.items[itemId];
      if (item && item.live && item.live.dish !== null)
        linked.push({ item, dish: item.live.dish });
    }
  }
  return linked;
}

/** Live dishes a suggestion offers (never counted as missing). */
function offeredIds(draft: IMenuDraft): Set<number> {
  const ids = new Set<number>();
  for (const item of Object.values(draft.items)) {
    if (item.live && item.live.dish === null)
      for (const id of item.live.candidateIds) ids.add(id);
  }
  return ids;
}

export interface ILiveSummaryCounts extends ISyncCounts {
  /** Visible dishes of the menu left out of the photos and kept. */
  kept: number;
  /** Linked dishes still held. */
  heldLeft: number;
  /** Every dish of the draft. */
  read: number;
}

/** The API's `syncCounts`, plus the dishes read. */
export function liveSummaryCounts(draft: IMenuDraft): ILiveSummaryCounts {
  const counts: ILiveSummaryCounts = {
    priceChanges: 0,
    added: 0,
    hidden: 0,
    shown: 0,
    kept: 0,
    heldLeft: 0,
    read: Object.keys(draft.items).length,
  };
  for (const { item, dish } of linkedItems(draft)) {
    const held = isHeld(item);
    if (held) counts.heldLeft++;
    else if (item.live && item.live.changes.length > 0) counts.priceChanges++;
    const outcome = linkedOutcome(item, dish);
    if (outcome.visibilityChanges) {
      if (outcome.visible) counts.shown++;
      else counts.hidden++;
    }
  }
  for (const item of Object.values(draft.items)) {
    if (isNewDish(item) && itemIsShown(draft, item) && itemIsPriced(item))
      counts.added++;
  }
  const live = draft.live;
  if (live) {
    const hiddenIds = new Set(live.hiddenIds);
    const offered = offeredIds(draft);
    for (const other of live.others) {
      if (!other.visible) continue;
      if (hiddenIds.has(other.itemId)) counts.hidden++;
      else if (!offered.has(other.itemId)) counts.kept++;
    }
  }
  return counts;
}

/** The summary card of an update (§3.5): the chips' counts and the others card's. */
export interface IUpdateSummary {
  /** «Parece un menú nuevo» waits for an answer: nothing is «Todo listo» yet. */
  newMenuOpen: boolean;
  /** The «Precio nuevo» chip. */
  priceChanged: number;
  /** The «Nuevos» chip. */
  added: number;
  /** Linked dishes still held. */
  heldLeft: number;
  /** The dishes of the others card. */
  others: number;
}

/** Publishing changes something: a price, a hide or show, or a dish to add. */
export function liveHasChanges(draft: IMenuDraft): boolean {
  const counts = liveSummaryCounts(draft);
  return (
    counts.priceChanges + counts.hidden + counts.shown > 0 ||
    Object.values(draft.items).some(isNewDish)
  );
}

/** Dishes diners will see after publishing (the dialog's count line). */
export function publishTotal(draft: IMenuDraft): number {
  let total = 0;
  for (const { item, dish } of linkedItems(draft)) {
    const outcome = linkedOutcome(item, dish);
    if (outcome.visible && outcome.price > 0) total++;
  }
  const live = draft.live;
  if (live) {
    const hiddenIds = new Set(live.hiddenIds);
    for (const other of live.others) {
      if (other.visible && !hiddenIds.has(other.itemId) && other.price > 0)
        total++;
    }
  }
  return total + liveSummaryCounts(draft).added;
}

export interface IOthersGroup {
  category: ILiveCategory;
  dishes: ILiveOther[];
}

export interface IOthersCardGroups {
  /** One group per photographed live category, in store order. */
  photographed: IOthersGroup[];
  /** Every other dish left out of the photos, in store order. */
  other: ILiveOther[];
  /** Every dish of the card. */
  total: number;
}

/**
 * The others card (§3.5): visible dishes of the menu left out of the photos
 * that no suggestion offers, by photographed category, then the rest.
 */
export function othersCardGroups(draft: IMenuDraft): IOthersCardGroups {
  const groups: IOthersCardGroups = { photographed: [], other: [], total: 0 };
  const live = draft.live;
  if (!live) return groups;
  const offered = offeredIds(draft);
  const dishes = live.others.filter(
    (other) => other.visible && !offered.has(other.itemId)
  );
  for (const category of live.categories) {
    const inCategory = dishes.filter(
      (other) => other.photographed && other.categoryId === category.id
    );
    if (inCategory.length > 0)
      groups.photographed.push({ category, dishes: inCategory });
  }
  groups.other = dishes.filter((other) => !other.photographed);
  groups.total = dishes.length;
  return groups;
}
